import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { IconEye, IconSend } from "@tabler/icons-react";
import { Button, Input, Select, Text } from "@/components/ui";
import { toastErrorApi } from "@/lib/toast";
import type { EstadoDisenador } from "../../../hooks/use-estado-disenador";
import { useEnviarPrueba, useGuardarBorrador } from "../../../hooks/use-notificaciones";
import { vistaPrevia, type EventoDelCatalogo, type Notificacion, type VistaPrevia } from "../../../services/notificaciones-service";
import { aBorrador } from "../../../utils/lienzo";

const ETIQUETA: Record<string, string> = {
  asunto: "Asunto", cuerpoHtml: "Cuerpo", cuerpoJson: "Cuerpo JSON", titulo: "Título", texto: "Texto", enlace: "Enlace",
  cuerpo: "Cuerpo", contentSid: "Plantilla (Content SID)", variables: "Valores de los marcadores", markdown: "MarkdownV2",
};

/** Qué se captura como destino de una prueba en cada canal (sin valor = el dato del propio usuario). */
const DESTINO_PRUEBA: Partial<Record<Notificacion["canal"], { etiqueta: string; placeholder: string; tipo: string }>> = {
  Email: { etiqueta: "Enviar prueba a", placeholder: "Tu correo (vacío = el de tu usuario)", tipo: "email" },
  Sms: { etiqueta: "Enviar prueba al número", placeholder: "+52… (vacío = tu celular del perfil)", tipo: "tel" },
  WhatsApp: { etiqueta: "Enviar prueba al número", placeholder: "+52… (vacío = tu celular del perfil)", tipo: "tel" },
  Telegram: { etiqueta: "Enviar prueba al chat", placeholder: "Chat id (vacío = tu Telegram vinculado)", tipo: "text" },
};

interface Props {
  flujoId: string;
  notificacion: Notificacion;
  eventos: EventoDelCatalogo[];
  disenador: EstadoDisenador;
}

/**
 * Vista previa con datos de ejemplo del evento (sin guardar) y envío de prueba real. La prueba
 * usa el borrador GUARDADO, así que si hay cambios se guardan antes de enviar.
 */
export function VistaPreviaYPrueba({ flujoId, notificacion: n, eventos, disenador: d }: Props) {
  const [evento, setEvento] = useState<string | undefined>(eventos[0]?.codigo);
  const [destino, setDestino] = useState("");
  const [preview, setPreview] = useState<VistaPrevia | null>(null);
  const guardar = useGuardarBorrador(flujoId);
  const prueba = useEnviarPrueba(flujoId);
  const generar = useMutation({ mutationFn: () => vistaPrevia(evento!, n), onSuccess: setPreview, onError: toastErrorApi });

  useEffect(() => {
    if (!evento || !eventos.some((e) => e.codigo === evento)) setEvento(eventos[0]?.codigo);
  }, [eventos, evento]);

  useEffect(() => setPreview(null), [n]);

  if (eventos.length === 0) return <Text size="xs" c="dimmed">Conecta un evento para generar la vista previa.</Text>;

  const enviar = async () => {
    if (d.sucio) await guardar.mutateAsync(aBorrador(d.estado));
    prueba.mutate({ notificacionClave: n.clave, destino: DESTINO_PRUEBA[n.canal] && destino.trim() ? destino.trim() : null, tipoEvento: evento ?? null });
  };

  return (
    <div className="space-y-2.5">
      {eventos.length > 1 ? (
        <Select size="xs" label="Datos de ejemplo de" value={evento} onValueChange={setEvento} options={eventos.map((e) => ({ value: e.codigo, label: e.nombre }))} />
      ) : null}
      <Button size="xs" variant="default" leftSection={<IconEye size={12} />} loading={generar.isPending} onClick={() => generar.mutate()} disabled={!evento}>
        Generar vista previa
      </Button>
      {preview ? (
        <div className="space-y-2 rounded-md border border-border p-2.5">
          {preview.errores.length > 0 ? (
            <ul className="list-disc pl-4 text-xs text-peligro-600">{preview.errores.map((e) => <li key={e}>{e}</li>)}</ul>
          ) : null}
          {Object.entries(preview.campos).map(([campo, valor]) => (
            <div key={campo}>
              <Text size="xs" c="dimmed">{ETIQUETA[campo] ?? campo}</Text>
              {campo === "cuerpoHtml" ? (
                // Contenido ya escapado por el backend y renderizado en un iframe aislado (sin scripts).
                <iframe title="Vista previa del correo" sandbox="" srcDoc={valor} className="h-48 w-full rounded border border-border bg-white" />
              ) : (
                <pre className="whitespace-pre-wrap break-all font-mono text-xs">{valor || "—"}</pre>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-1.5 border-t border-border pt-2.5">
        {DESTINO_PRUEBA[n.canal] ? (
          <Input
            size="xs"
            type={DESTINO_PRUEBA[n.canal]!.tipo}
            label={DESTINO_PRUEBA[n.canal]!.etiqueta}
            placeholder={DESTINO_PRUEBA[n.canal]!.placeholder}
            value={destino}
            onChange={(e) => setDestino(e.target.value)}
          />
        ) : (
          <Text size="xs" c="dimmed">
            {n.canal === "Webhook"
              ? "La prueba se envía a la URL del webhook."
              : n.canal === "WebPush"
                ? "La prueba llega a los navegadores donde activaste las notificaciones (Mi perfil)."
                : "La prueba llega a tu propia campana."}
          </Text>
        )}
        <Button size="xs" leftSection={<IconSend size={12} />} loading={guardar.isPending || prueba.isPending} onClick={() => void enviar().catch(() => undefined)}>
          {d.sucio ? "Guardar y enviar prueba" : "Enviar prueba"}
        </Button>
      </div>
    </div>
  );
}
